import {
  useMemo,
  useRef,
  useState,
  useEffect,
  type CSSProperties,
  type MouseEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import RadarChart, {
  type RadarDataPoint,
} from '../components/ui/Charts/RadarChart/RadarChart';
import {
  DimensionLineChart,
  type DimensionData,
} from '../components/ui/Charts/LineChart/LineChart';
import DimensionLabel from '../components/ui/DimensionLabel/DimensionLabel';
import { SubmissionSlider } from '../components/ui/SubmissionSlider';

import {
  dimensionsService,
  type Dimension,
  type Submission,
  type Project,
  type ProjectListItem,
} from '../services/dimensionsService';
import {
  feedbackService,
  type FeedbackResponse,
  type DimensionScore as DetailedDimensionScore,
} from '../services/feedbackService';
import { formatEnglishDate } from '../lib/dateFormat';

import './Home.css';

interface DimensionGroupDefinition {
  key: string;
  label: string;
  dimensionIds: number[];
}

interface FeedbackPanelDimension {
  id: string;
  label: string;
  shortLabel: string;
  color: string;
  personalScore: number;
  classAvgScore: number;
  feedback: string;
  nextStep: string;
}

interface FeedbackPanelGroup {
  key: string;
  label: string;
  color: string;
  dimensions: FeedbackPanelDimension[];
}

const normalizeScore = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return 0;
};

const DIMENSION_GROUPS: DimensionGroupDefinition[] = [
  {
    key: 'purposefulness',
    label: 'Purposefulness',
    dimensionIds: [1, 2],
  },
  {
    key: 'disciplinary-grounding',
    label: 'Disciplinary Grounding',
    dimensionIds: [3, 4, 5],
  },
  {
    key: 'integration',
    label: 'Integration',
    dimensionIds: [6, 7],
  },
  {
    key: 'critical-awareness',
    label: 'Critical Awareness',
    dimensionIds: [8, 9],
  },
];

const Home = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId?: string }>();

  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [detailedFeedback, setDetailedFeedback] = useState<FeedbackResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [highlightedDimId, setHighlightedDimId] = useState<string | null>(null);
  const [expandedFeedbackDimId, setExpandedFeedbackDimId] = useState<string | null>(null);
  const [isProjectListDragging, setIsProjectListDragging] = useState(false);
  const projectListRef = useRef<HTMLDivElement | null>(null);
  const projectListDragStartXRef = useRef(0);
  const projectListScrollLeftRef = useRef(0);

  useEffect(() => {
    const fetchDimensions = async () => {
      try {
        const dims = await dimensionsService.getDimensions();
        setDimensions(dims);
      } catch (err) {
        console.error('Error fetching dimensions:', err);
        setError('Failed to load dimensions');
      }
    };

    fetchDimensions();
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const projectsList = await dimensionsService.getProjects();
        setProjects(projectsList);

        if (projectId) {
          const selectedProj = projectsList.find((project) => project.id === projectId);

          if (selectedProj) {
            await loadProjectSubmissions(selectedProj.id);
          } else {
            setError('Project not found');
            setLoading(false);
          }
        } else if (projectsList.length > 0) {
          await loadProjectSubmissions(projectsList[0].id);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects');
        setLoading(false);
      }
    };

    fetchProjects();
  }, [projectId]);

  const loadProjectSubmissions = async (nextProjectId: string) => {
    try {
      setLoading(true);
      setDetailedFeedback(null);

      const data = await dimensionsService.getProjectSubmissions(nextProjectId);
      setSelectedProject(data.project);
      setSubmissions(data.submissions);

      if (data.submissions.length > 0) {
        setSelectedSubmissionId(data.submissions[data.submissions.length - 1].id);
      } else {
        setSelectedSubmissionId(null);
      }
    } catch (err) {
      console.error('Error fetching project submissions:', err);
      setError('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = async (nextProjectId: string) => {
    setHighlightedDimId(null);
    setExpandedFeedbackDimId(null);
    await loadProjectSubmissions(nextProjectId);
  };

  useEffect(() => {
    const loadDetailedFeedback = async () => {
      if (!selectedSubmissionId) {
        setDetailedFeedback(null);
        return;
      }

      try {
        const feedback = await feedbackService.getFeedback(selectedSubmissionId);
        setDetailedFeedback(feedback);
      } catch (err) {
        console.error('[Dashboard] Error loading detailed feedback:', err);
        setDetailedFeedback(null);
      }
    };

    loadDetailedFeedback();
  }, [selectedSubmissionId]);

  useEffect(() => {
    setExpandedFeedbackDimId(null);
  }, [selectedSubmissionId]);

  const currentSubmission = useMemo(
    () => submissions.find((submission) => submission.id === selectedSubmissionId),
    [selectedSubmissionId, submissions]
  );

  const currentSubmissionIndex = useMemo(
    () => submissions.findIndex((submission) => submission.id === selectedSubmissionId),
    [selectedSubmissionId, submissions]
  );

  const detailedFeedbackByDimension = useMemo(() => {
    const byDimension: Record<string, DetailedDimensionScore> = {};

    detailedFeedback?.dimensions.forEach((dimension) => {
      byDimension[dimension.dimension_id.toString()] = dimension;
    });

    return byDimension;
  }, [detailedFeedback]);

  const currentSubmissionAverageScore = useMemo(() => {
    if (!currentSubmission) {
      return null;
    }

    const scores = Object.values(currentSubmission.scores)
      .map((score) => normalizeScore(score.personal))
      .filter((score) => typeof score === 'number');

    if (scores.length === 0) {
      return null;
    }

    const total = scores.reduce((sum, score) => sum + score, 0);
    return (total / scores.length).toFixed(1);
  }, [currentSubmission]);

  const radarData: RadarDataPoint[] = useMemo(() => {
    if (!currentSubmission) {
      return [];
    }

    return dimensions.map((dimension) => {
      const score = currentSubmission.scores[dimension.id.toString()];

      return {
        dimension: dimension.short_label || dimension.label,
        userScore: normalizeScore(score?.personal),
        classAverage: normalizeScore(score?.classAvg),
      };
    });
  }, [currentSubmission, dimensions]);

  const lineChartData = useMemo(() => {
    return submissions.map((submission) => {
      const point: DimensionData = {
        submission: submission.name,
      };

      dimensions.forEach((dimension) => {
        point[dimension.id.toString()] = normalizeScore(
          submission.scores[dimension.id.toString()]?.personal
        );
      });

      return point;
    });
  }, [dimensions, submissions]);

  const chartDimsForLine = useMemo(() => {
    return dimensions.map((dimension) => ({
      id: dimension.id.toString(),
      text: dimension.short_label || dimension.label,
      color: dimension.color_hex,
      opacity:
        highlightedDimId === null || highlightedDimId === dimension.id.toString()
          ? 1
          : 0.15,
      strokeWidth: highlightedDimId === dimension.id.toString() ? 3 : 2,
    }));
  }, [dimensions, highlightedDimId]);

  const sliderItems = useMemo(() => {
    return submissions.map((submission) => ({
      id: submission.id,
      name: submission.name,
      date: submission.date,
    }));
  }, [submissions]);

  const groupedFeedback = useMemo<FeedbackPanelGroup[]>(() => {
    return DIMENSION_GROUPS.map((group) => {
      const groupDimensions = group.dimensionIds
        .map((dimensionId) => dimensions.find((dimension) => dimension.id === dimensionId))
        .filter((dimension): dimension is Dimension => Boolean(dimension))
        .map((dimension) => {
          const dimensionId = dimension.id.toString();
          const baseScore = currentSubmission?.scores[dimensionId];
          const detailedDimension = detailedFeedbackByDimension[dimensionId];
          const nextStep =
            detailedDimension?.ai_improvements?.[0] ||
            baseScore?.ai_improvements?.[0] ||
            'Detailed next-step guidance is not available yet.';

          return {
            id: dimensionId,
            label: dimension.label,
            shortLabel: dimension.short_label || dimension.label,
            color: dimension.color_hex,
            personalScore: normalizeScore(
              detailedDimension?.personal_score ??
                detailedDimension?.ai_score ??
                baseScore?.personal
            ),
            classAvgScore: normalizeScore(baseScore?.classAvg),
            feedback:
              detailedDimension?.ai_reasoning ||
              baseScore?.ai_reasoning ||
              'AI feedback for this rubric will appear here once available.',
            nextStep,
          };
        });

      return {
        key: group.key,
        label: group.label,
        color: groupDimensions[0]?.color || 'var(--color-blue-ntu)',
        dimensions: groupDimensions,
      };
    }).filter((group) => group.dimensions.length > 0);
  }, [currentSubmission, detailedFeedbackByDimension, dimensions]);

  const formatProjectChipDate = (date: string) => {
    return formatEnglishDate(date, {
      month: 'short',
      day: 'numeric',
    });
  };

  const handleProjectListMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (!projectListRef.current) {
      return;
    }

    setIsProjectListDragging(true);
    projectListDragStartXRef.current = event.clientX;
    projectListScrollLeftRef.current = projectListRef.current.scrollLeft;
  };

  const handleProjectListMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (!isProjectListDragging || !projectListRef.current) {
      return;
    }

    const deltaX = event.clientX - projectListDragStartXRef.current;
    projectListRef.current.scrollLeft = projectListScrollLeftRef.current - deltaX;
  };

  const stopProjectListDragging = () => {
    setIsProjectListDragging(false);
  };

  const toggleDimHighlight = (dimensionId: string) => {
    setHighlightedDimId((previous) => (previous === dimensionId ? null : dimensionId));
  };

  const toggleFeedbackRow = (dimensionId: string) => {
    setHighlightedDimId(dimensionId);
    setExpandedFeedbackDimId((previous) => (previous === dimensionId ? null : dimensionId));
  };

  const handleViewReport = () => {
    if (selectedProject) {
      navigate(`/project/${selectedProject.id}`);
    }
  };

  if (loading && dimensions.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="subtitle-2 text-[var(--color-grey-55)]">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="subtitle-2 text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-grey-05)]">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="heading-3 mb-2">Dashboard</h1>
          <p className="body text-[var(--color-grey-55)]">
            Track your project performance across multiple dimensions
          </p>
        </div>

        <div className="dashboard-card p-4 mb-5">
          <label className="caption text-[var(--color-grey-55)] mb-2 block">Projects</label>
          <div
            ref={projectListRef}
            className={
              isProjectListDragging
                ? 'flex cursor-grabbing items-center gap-2 overflow-x-auto pb-1 select-none'
                : 'flex cursor-grab items-center gap-2 overflow-x-auto pb-1'
            }
            onMouseDown={handleProjectListMouseDown}
            onMouseMove={handleProjectListMouseMove}
            onMouseUp={stopProjectListDragging}
            onMouseLeave={stopProjectListDragging}
          >
            <button
              type="button"
              onClick={() => navigate('/project/new')}
              className="dashboard-project-add"
              aria-label="Create project"
              title="Create project"
            >
              <span className="dashboard-project-add__icon">+</span>
              <span className="dashboard-project-add__label">New Project</span>
            </button>
            {projects.map((project) => {
              const isSelected = selectedProject?.id === project.id;
              const projectDate = formatProjectChipDate(project.date);

              return (
                <button
                  key={project.id}
                  type="button"
                  onClick={() => handleProjectChange(project.id)}
                  className={isSelected ? 'dashboard-project-chip is-active' : 'dashboard-project-chip'}
                >
                  <span>{project.name}</span>
                  {projectDate && <span className="dashboard-project-chip__date">{projectDate}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {selectedProject && (
          <div className="dashboard-summary-card">
            <div className="dashboard-summary-card__icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
              </svg>
            </div>
            <div className="dashboard-summary-card__content">
              <div className="dashboard-summary-card__title">{selectedProject.name}</div>
              <div className="dashboard-summary-card__sub">
                {selectedProject.summary || 'Project overview'}
                {currentSubmission && (
                  <span>
                    {' '}
                    · Last submitted{' '}
                    {formatEnglishDate(currentSubmission.date, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                )}
              </div>
            </div>
            <div className="dashboard-summary-card__stats">
              <div className="dashboard-summary-card__stat">
                <div className="dashboard-summary-card__value">{submissions.length}</div>
                <div className="dashboard-summary-card__label">Submissions</div>
              </div>
              <div className="dashboard-summary-card__stat">
                <div className="dashboard-summary-card__value">{dimensions.length}</div>
                <div className="dashboard-summary-card__label">Dimensions</div>
              </div>
              <div className="dashboard-summary-card__stat">
                <div className="dashboard-summary-card__value">
                  {currentSubmissionAverageScore ?? '—'}
                </div>
                <div className="dashboard-summary-card__label">Avg Score</div>
              </div>
            </div>
          </div>
        )}

        {!selectedProject || submissions.length === 0 ? (
          <div className="dashboard-card p-12 flex flex-col items-center justify-center mt-5">
            <p className="subtitle-2 text-[var(--color-grey-55)] mb-2">
              {projects.length === 0 ? 'No projects found' : 'No submissions found for this project'}
            </p>
            <p className="caption text-[var(--color-grey-55)]">
              {projects.length === 0
                ? 'Create a project to get started'
                : 'Submit your first draft to see analytics'}
            </p>
          </div>
        ) : (
          <div className="dashboard-main-grid">
            <section className="dashboard-panel dashboard-panel--radar">
              <div className="dashboard-panel__header">
                <div>
                  <h3 className="dashboard-panel__title">Dimension Radar</h3>
                  <p className="dashboard-panel__sub">
                    {currentSubmissionIndex >= 0
                      ? `Submission ${currentSubmissionIndex + 1}`
                      : 'Select a submission'}
                  </p>
                </div>
              </div>

              <SubmissionSlider
                submissions={sliderItems}
                selectedId={selectedSubmissionId}
                onSelect={setSelectedSubmissionId}
              />

              <div className="dashboard-radar-box">
                {radarData.length === 0 ? (
                  <div className="dashboard-empty-state">
                    <p className="dashboard-empty-state__title">No data available</p>
                  </div>
                ) : (
                  <RadarChart
                    data={radarData}
                    selectedDimensions={dimensions.map((dimension) => dimension.short_label || dimension.label)}
                    maxScore={3}
                    height={260}
                    userName="Yours"
                    classAverageName="Class Avg"
                  />
                )}
              </div>
            </section>

            <section className="dashboard-feedback-panel">
              <div className="dashboard-feedback-panel__header">
                <div>
                  <h3 className="dashboard-feedback-panel__title">Dimension Feedback</h3>
                  <p className="dashboard-feedback-panel__sub">
                    {currentSubmissionIndex >= 0
                      ? `Submission ${currentSubmissionIndex + 1}`
                      : 'Latest submission'}{' '}
                    · {dimensions.length} dimensions
                  </p>
                  <p className="dashboard-feedback-panel__score-key">
                    Scores shown as <strong>Your score</strong> / <strong>Class avg</strong>
                  </p>
                </div>
                <button
                  type="button"
                  className="dashboard-feedback-panel__report-link"
                  onClick={handleViewReport}
                >
                  View full report
                </button>
              </div>

              <div className="dashboard-feedback-panel__body">
                {groupedFeedback.map((group) => (
                  <section key={group.key} className="dashboard-feedback-group">
                    <div className="dashboard-feedback-group__header">
                      <span
                        className="dashboard-feedback-group__dot"
                        style={{ backgroundColor: group.color }}
                      />
                      <span
                        className="dashboard-feedback-group__label"
                        style={{ color: group.color }}
                      >
                        {group.label}
                      </span>
                    </div>

                    <div className="dashboard-feedback-group__rows">
                      {group.dimensions.map((dimension) => {
                        const isHighlighted = highlightedDimId === dimension.id;
                        const isDimmed = highlightedDimId !== null && highlightedDimId !== dimension.id;
                        const isExpanded = expandedFeedbackDimId === dimension.id;
                        const personalPct = (dimension.personalScore / 3) * 100;
                        const classAvgPct = (dimension.classAvgScore / 3) * 100;

                        return (
                          <div
                            key={dimension.id}
                            className={[
                              'dashboard-feedback-row',
                              isHighlighted ? 'is-highlighted' : '',
                              isDimmed ? 'is-dimmed' : '',
                              isExpanded ? 'is-expanded' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => toggleFeedbackRow(dimension.id)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                toggleFeedbackRow(dimension.id);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-expanded={isExpanded}
                            style={
                              isHighlighted
                                ? ({
                                    boxShadow: `inset 3px 0 0 ${dimension.color}`,
                                  } as CSSProperties)
                                : undefined
                            }
                          >
                            <div className="dashboard-feedback-row__top">
                              <div className="dashboard-feedback-row__name">
                                {dimension.shortLabel}
                              </div>
                              <div
                                className="dashboard-feedback-row__score"
                                aria-label={`Your score ${dimension.personalScore.toFixed(1)}, class average ${dimension.classAvgScore.toFixed(1)}`}
                                title={`Your score: ${dimension.personalScore.toFixed(1)} | Class avg: ${dimension.classAvgScore.toFixed(1)}`}
                              >
                                <span
                                  className="dashboard-feedback-row__score-primary"
                                  style={{ color: dimension.color }}
                                >
                                  {dimension.personalScore.toFixed(1)}
                                </span>
                                <span className="dashboard-feedback-row__score-sep">
                                  {' '}
                                  / {dimension.classAvgScore.toFixed(1)}
                                </span>
                              </div>
                              <div className="dashboard-feedback-row__toggle" aria-hidden="true">
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="6 9 12 15 18 9" />
                                </svg>
                              </div>
                            </div>

                            <div className="dashboard-feedback-row__bar">
                              <div
                                className="dashboard-feedback-row__bar-fill"
                                style={{
                                  width: `${personalPct}%`,
                                  backgroundColor: dimension.color,
                                }}
                              />
                              <div
                                className="dashboard-feedback-row__bar-marker"
                                style={{ left: `${classAvgPct}%` }}
                              />
                            </div>

                            <div className="dashboard-feedback-row__insights">
                              <div className="dashboard-feedback-row__insight dashboard-feedback-row__insight--feedback">
                                <span className="dashboard-feedback-row__insight-label">Feedback</span>
                                <div
                                  className={
                                    isExpanded
                                      ? 'dashboard-feedback-row__insight-text'
                                      : 'dashboard-feedback-row__insight-text is-collapsed'
                                  }
                                >
                                  {dimension.feedback}
                                </div>
                              </div>
                              <div className="dashboard-feedback-row__insight dashboard-feedback-row__insight--next">
                                <span className="dashboard-feedback-row__insight-label">Next Step</span>
                                <div
                                  className={
                                    isExpanded
                                      ? 'dashboard-feedback-row__insight-text'
                                      : 'dashboard-feedback-row__insight-text is-collapsed'
                                  }
                                >
                                  {dimension.nextStep}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            </section>

            <section className="dashboard-panel dashboard-panel--history">
              <div className="dashboard-panel__header">
                <div>
                  <h3 className="dashboard-panel__title">Submission History</h3>
                  <p className="dashboard-panel__sub">Click a rubric to highlight it</p>
                </div>
              </div>

              <div className="dashboard-history-box">
                {lineChartData.length === 0 ? (
                  <div className="dashboard-empty-state">
                    <p className="dashboard-empty-state__title">No submissions found</p>
                  </div>
                ) : (
                  <DimensionLineChart
                    data={lineChartData}
                    dimensions={chartDimsForLine}
                    title=""
                    showArea={false}
                    showLegend={false}
                    referenceLineY={null}
                    height={200}
                    yAxisDomain={[0, 3]}
                    yAxisTicks={[0, 1, 2, 3]}
                    className="dashboard-history-chart"
                  />
                )}
              </div>

              <div className="dashboard-dim-tags">
                {dimensions.map((dimension) => {
                  const isHighlighted = highlightedDimId === dimension.id.toString();
                  const isDimmed =
                    highlightedDimId !== null && highlightedDimId !== dimension.id.toString();

                  return (
                    <DimensionLabel
                      key={dimension.id}
                      text={dimension.short_label || dimension.label}
                      color={dimension.color_hex}
                      size="small"
                      isSelected={isHighlighted}
                      onClick={() => toggleDimHighlight(dimension.id.toString())}
                      className={isDimmed ? 'dashboard-dim-tag--dimmed' : ''}
                    />
                  );
                })}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
